import './calendar.scss';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { jwtDecode } from 'jwt-decode';
import { setDayjsLocale } from '../../../../utils/setDayjsLocale';
import { getPickersLocaleText } from '../../../../utils/muiPickersLocaleText';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import dayjs from 'dayjs';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import Badge from '@mui/material/Badge';
import ModalTask from '../../common/modal-task/modal-task';
import ModalTaskView from '../modal-task-view/modal-task-view';
import ModalWindow from '../modal/modal';
import apiClient from '../../../../utils/apiClient';

function Calendar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const token = sessionStorage.getItem('token') || null;
  const decodedToken = jwtDecode(token);
  const loggedUser = JSON.parse(sessionStorage.getItem('loggedUser') || 'null');

  const [pickersKey, setPickersKey] = React.useState('en');
  const [adapterLocale, setAdapterLocale] = React.useState('en');

  const [task, setTask] = React.useState(null);
  const [modalOpenTask, setModalOpenTask] = React.useState(false);

  const [taskView, setTaskView] = React.useState(null);
  const [modalOpenTaskView, setModalOpenTaskView] = React.useState(false);

  const [tasks, setTasks] = React.useState({});

  const [categories, setCategories] = React.useState([]);
  const [catChecks, setCatChecks] = React.useState({});

  const [onlyWithHacs, setOnlyWithHacs] = React.useState(false);
  const [showSystemTasks, setShowSystemTasks] = React.useState(true);

  const [reloadKey, setReloadKey] = React.useState(0);
  const refreshWeek = React.useCallback(() => setReloadKey((k) => k + 1), []);

  const [openRemoveModal, setOpenRemoveModal] = React.useState(false);
  const [removeEntityId, setRemoveEntityId] = React.useState(null);

  const closeModalTask = () => {
    setModalOpenTask(false);
    setTask(null);
  };

  const closeModalTaskView = () => {
    setModalOpenTaskView(false);
    setTaskView(null);
  };

  const closeRemoveModal = () => {
    setOpenRemoveModal(false);
    setRemoveEntityId(null);
  };

  React.useEffect(() => {
    let alive = true;

    (async () => {
      const { adapterLocale, pickersLocaleKey } = await setDayjsLocale(i18n.resolvedLanguage || i18n.language);
      if (!alive) return;
      setAdapterLocale(adapterLocale);
      setPickersKey(pickersLocaleKey);
    })();

    return () => {
      alive = false;
    };
  }, [i18n.resolvedLanguage, i18n.language]);

  const localeText = React.useMemo(() => getPickersLocaleText(pickersKey), [pickersKey]);

  const weekRef = React.useRef(null);

  const [selectedDate, setSelectedDate] = React.useState(dayjs());

  const getMonday = React.useCallback((date) => {
    const d = date.day();
    const diffFromMonday = (d + 6) % 7;
    return date.subtract(diffFromMonday, 'day');
  }, []);

  const weekDays = React.useMemo(() => {
    const monday = getMonday(selectedDate);
    return Array.from({ length: 5 }, (_, i) => monday.add(i, 'day'));
  }, [selectedDate, getMonday]);

  const weekLabel = React.useMemo(() => {
    const monday = getMonday(selectedDate);
    const friday = monday.add(4, 'day');

    const sameMonth = monday.month() === friday.month();
    const sameYear = monday.year() === friday.year();

    if (sameMonth && sameYear) return `${monday.format('MMM D')}–${friday.format('D, YYYY')}`;
    if (!sameMonth && sameYear) return `${monday.format('MMM D')}–${friday.format('MMM D, YYYY')}`;
    return `${monday.format('MMM D, YYYY')}–${friday.format('MMM D, YYYY')}`;
  }, [selectedDate, getMonday]);

  const allDayHeight = React.useMemo(() => {
    const TASK_H = 24;
    const TASK_MB = 2;
    const MIN = 30;

    const maxTasks = weekDays.reduce((max, d) => {
      const key = d.format('YYYY-MM-DD');
      const count = tasks[key]?.allDay?.length ?? 0;
      return Math.max(max, count);
    }, 0);

    return Math.max(MIN, maxTasks * (TASK_H + TASK_MB));
  }, [weekDays, tasks]);

  const isInSelectedWeek = (day) => {
    if (!selectedDate) return false;

    const monday = getMonday(selectedDate);
    const friday = monday.add(4, 'day');

    return (
      day.isSame(monday, 'day') ||
      day.isSame(friday, 'day') ||
      (day.isAfter(monday, 'day') && day.isBefore(friday, 'day'))
    );
  };

  const handleDateChange = (newValue) => {
    if (!newValue) return;
    setSelectedDate(newValue);
  };

  const handleTodayClick = () => {
    setSelectedDate(dayjs());
  };

  React.useEffect(() => {
    const container = weekRef.current;
    if (!container) return;

    const now = new Date();
    const currentHour = now.getHours() - 1;

    const hourEl = container.querySelector(`.hour[data-hour="${currentHour}"]`);
    if (!hourEl) return;

    const containerRect = container.getBoundingClientRect();
    const hourRect = hourEl.getBoundingClientRect();
    const offset = hourRect.top - containerRect.top + container.scrollTop;

    container.scrollTo({ top: offset });
  }, []);

  const getCategoryNumber = React.useCallback((ev) => {
    const raw = ev?.category_id ?? ev?.categoryId ?? ev?.category ?? ev?.cat ?? 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  }, []);

  const toHHmm = React.useCallback((timeRaw) => {
    if (!timeRaw || typeof timeRaw !== 'string') return '';
    const parts = timeRaw.split(':');
    if (parts.length < 2) return '';
    const hh = String(parts[0]).padStart(2, '0');
    const mm = String(parts[1]).padStart(2, '0');
    return `${hh}:${mm}`;
  }, []);

  const timeToMinutes = React.useCallback((hhmm) => {
    if (!hhmm || typeof hhmm !== 'string' || !hhmm.includes(':')) return Number.POSITIVE_INFINITY;
    const [hh, mm] = hhmm.split(':');
    const h = Number(hh);
    const m = Number(mm);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return Number.POSITIVE_INFINITY;
    return h * 60 + m;
  }, []);

  const isSystemCreated = React.useCallback((ev) => {
    const v = ev?.created_by_id ?? ev?.created_by ?? ev?.createdBy ?? null;
    return String(v ?? '').toUpperCase() === 'SYSTEM';
  }, []);

  const hasSystemBadgeType = React.useCallback((ev) => {
    const v = ev?.system_task_type;
    const n = Number(v);
    return n === 1 || n === 2 || v === '1' || v === '2';
  }, []);

  React.useEffect(() => {
    if (!token) return;

    let alive = true;

    (async () => {
      try {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const res = await apiClient.get(`${process.env.REACT_APP_API_URL}/calendar/categories`, config);

        if (!alive) return;

        const rows = Array.isArray(res?.data) ? res.data : [];
        const activeCats = rows
          .filter((c) => Number(c?.active) === 1)
          .map((c) => ({
            id: Number(c.id),
            label: String(c.category ?? '').trim(),
          }))
          .filter((c) => Number.isFinite(c.id) && c.label);

        setCategories(activeCats);

        setCatChecks((prev) => {
          const next = { ...(prev || {}) };
          activeCats.forEach((c) => {
            if (next[c.id] === undefined) next[c.id] = true;
          });
          return next;
        });
      } catch (e) {
        if (!alive) return;
        setCategories([]);
      }
    })();

    return () => {
      alive = false;
    };
  }, [token]);

  const buildWeekTasks = React.useCallback(
    (events, monday) => {
      const next = {};
      const weekStart = dayjs(monday).startOf('day');
      const weekEnd = dayjs(monday).add(4, 'day').startOf('day');

      for (let i = 0; i < 5; i++) {
        const key = dayjs(monday).add(i, 'day').format('YYYY-MM-DD');
        next[key] = { allDay: [], hours: {} };
      }

      (Array.isArray(events) ? events : []).forEach((ev) => {
        const startDateRaw = ev.starting_date ?? ev.startDate ?? ev.date ?? null;
        const endDateRaw = ev.ending_date ?? ev.endDate ?? startDateRaw ?? null;

        if (!startDateRaw) return;

        const start = dayjs(startDateRaw).startOf('day');
        const end = dayjs(endDateRaw).startOf('day');
        if (!start.isValid() || !end.isValid()) return;

        const rangeStart = start.isAfter(weekStart) ? start : weekStart;
        const rangeEnd = end.isBefore(weekEnd) ? end : weekEnd;
        if (rangeEnd.isBefore(rangeStart)) return;

        if (onlyWithHacs && ev?.fromMyHacs !== true) return;
        if (!showSystemTasks && isSystemCreated(ev)) return;

        const categoryNum = getCategoryNumber(ev);
        if (!catChecks?.[categoryNum]) return;

        const allDay = Boolean(ev.all_day ?? ev.allDay);
        const timeLabel = toHHmm(ev.starting_time ?? ev.time ?? '');
        const title = ev.title ?? ev.text ?? '';

        let hourKey = null;
        let minutes = Number.POSITIVE_INFINITY;

        if (!allDay && timeLabel) {
          hourKey = String(Number(timeLabel.split(':')[0]));
          if (hourKey === 'NaN') hourKey = null;
          minutes = timeToMinutes(timeLabel);
        }

        for (let d = rangeStart; d.isSame(rangeEnd, 'day') || d.isBefore(rangeEnd, 'day'); d = d.add(1, 'day')) {
          const dayKey = d.format('YYYY-MM-DD');
          if (!next[dayKey]) continue;

          const item = {
            id: `${ev.id}-${dayKey}`,
            text: title,
            color: ev.color ?? '',
            category: categoryNum,
            timeLabel,
            minutes,
            raw: ev,
          };

          if (allDay || !hourKey) {
            next[dayKey].allDay.push(item);
          } else {
            if (!next[dayKey].hours[hourKey]) next[dayKey].hours[hourKey] = [];
            next[dayKey].hours[hourKey].push(item);
          }
        }
      });

      Object.keys(next).forEach((dayKey) => {
        const hours = next[dayKey].hours || {};
        Object.keys(hours).forEach((hk) => {
          hours[hk].sort((a, b) => (a.minutes ?? Number.POSITIVE_INFINITY) - (b.minutes ?? Number.POSITIVE_INFINITY));
        });
      });

      return next;
    },
    [getCategoryNumber, toHHmm, timeToMinutes, onlyWithHacs, catChecks, showSystemTasks, isSystemCreated]
  );

  React.useEffect(() => {
    if (!token) return;

    const monday = getMonday(selectedDate);
    const friday = monday.add(4, 'day');

    const start_date = monday.format('YYYY-MM-DD');
    const end_date = friday.format('YYYY-MM-DD');

    const config = { headers: { Authorization: `Bearer ${token}` } };

    let alive = true;

    (async () => {
      try {
        const res = await apiClient.get(
          `${process.env.REACT_APP_API_URL}/calendar/events?start_date=${start_date}&end_date=${end_date}`,
          config
        );

        if (!alive) return;

        const events = Array.isArray(res.data) ? res.data : res.data?.events ?? [];
        setTasks(buildWeekTasks(events, monday));
      } catch (e) {
        if (!alive) return;
        setTasks(buildWeekTasks([], monday));
      }
    })();

    return () => {
      alive = false;
    };
  }, [selectedDate, token, getMonday, buildWeekTasks, reloadKey]);

  const [badgeDays, setBadgeDays] = React.useState([]);
  const [badgeLoading, setBadgeLoading] = React.useState(false);

  React.useEffect(() => {
    if (!token) return;

    const monthStart = dayjs(selectedDate).startOf('month');
    const monthEnd = dayjs(selectedDate).endOf('month');

    const start_date = monthStart.format('YYYY-MM-DD');
    const end_date = monthEnd.format('YYYY-MM-DD');

    const config = { headers: { Authorization: `Bearer ${token}` } };

    let alive = true;
    setBadgeLoading(true);

    (async () => {
      try {
        const res = await apiClient.get(
          `${process.env.REACT_APP_API_URL}/calendar/events?start_date=${start_date}&end_date=${end_date}`,
          config
        );

        if (!alive) return;

        const events = Array.isArray(res.data) ? res.data : res.data?.events ?? [];

        const daysSet = new Set();

        (Array.isArray(events) ? events : []).forEach((ev) => {
          if (!hasSystemBadgeType(ev)) return;
          if (onlyWithHacs && ev?.fromMyHacs !== true) return;
          if (!showSystemTasks && isSystemCreated(ev)) return;

          const categoryNum = getCategoryNumber(ev);
          if (!catChecks?.[categoryNum]) return;

          const startRaw = ev.starting_date ?? ev.startDate ?? ev.date ?? null;
          const endRaw = ev.ending_date ?? ev.endDate ?? startRaw ?? null;
          if (!startRaw) return;

          const start = dayjs(startRaw).startOf('day');
          const end = dayjs(endRaw).startOf('day');
          if (!start.isValid() || !end.isValid()) return;

          const rangeStart = start.isAfter(monthStart, 'day') ? start : monthStart.startOf('day');
          const rangeEnd = end.isBefore(monthEnd, 'day') ? end : monthEnd.startOf('day');
          if (rangeEnd.isBefore(rangeStart)) return;

          for (let d = rangeStart; d.isSame(rangeEnd, 'day') || d.isBefore(rangeEnd, 'day'); d = d.add(1, 'day')) {
            daysSet.add(d.date());
          }
        });

        setBadgeDays(Array.from(daysSet));
        setBadgeLoading(false);
      } catch (e) {
        if (!alive) return;
        setBadgeDays([]);
        setBadgeLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [
    selectedDate,
    token,
    reloadKey,
    onlyWithHacs,
    showSystemTasks,
    catChecks,
    getCategoryNumber,
    hasSystemBadgeType,
    isSystemCreated,
  ]);

  const CalendarDayWithBadge = (props) => {
    const { day, outsideCurrentMonth, highlightedDays = [], ...other } = props;
    const show = !outsideCurrentMonth && highlightedDays.includes(day.date());

    return (
      <Badge
        overlap="circular"
        variant={show ? 'dot' : 'standard'}
        badgeContent={undefined}
      >
        <PickersDay {...other} outsideCurrentMonth={outsideCurrentMonth} day={day} />
      </Badge>
    );
  };

  const CustomDay = (dayProps) => {
    const { day } = dayProps;
    const inWeek = isInSelectedWeek(day);
    return (
      <CalendarDayWithBadge
        {...dayProps}
        className={`${dayProps.className || ''} ${inWeek ? 'selected-week-day' : ''}`}
      />
    );
  };

  const openTaskView = (taskItem) => {
    setTaskView(taskItem?.raw ?? null);
    setModalOpenTaskView(true);
  };

  const handleEditFromView = (fullTask) => {
    setModalOpenTaskView(false);
    setTaskView(null);
    setTask(fullTask || null);
    setModalOpenTask(true);
  };

  const handleRemoveFromView = (fullTask) => {
    const id = fullTask?.id ?? fullTask?.raw?.id ?? null;
    if (!id) return;

    setModalOpenTaskView(false);
    setTaskView(null);

    setRemoveEntityId(id);
    setOpenRemoveModal(true);
  };

  const executeDeleteTask = async (tokenArg, _userId, entityId) => {
    if (!tokenArg) throw new Error('Missing token');
    if (!entityId) throw new Error('Missing entityId');

    await apiClient.delete(`${process.env.REACT_APP_API_URL}/calendar/event/${entityId}`, {
      headers: { Authorization: `Bearer ${tokenArg}` },
    });
  };

  const postDeleteTask = () => {
    refreshWeek();
  };

  const isCompletedRaw = (raw) => raw?.completed === true || raw?.completed === 1 || raw?.completed === '1';

  return (
    <div className="calendar">
      <aside>
        <div className="actions">
          <button
            type="button"
            className="btn primary fit small"
            onClick={() => {
              setTask(null);
              setModalOpenTask(true);
            }}
          >
            New reminder
          </button>
        </div>

        <div>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale} localeText={localeText}>
            <DateCalendar
              value={selectedDate}
              onChange={handleDateChange}
              onMonthChange={(d) => setSelectedDate((prev) => prev.year(d.year()).month(d.month()))}
              loading={badgeLoading}
              slots={{ day: CustomDay }}
              slotProps={{ day: { highlightedDays: badgeDays } }}
            />
          </LocalizationProvider>
        </div>

        <div className="filters">
          <div>
            <strong>{t('calendar-filter-1')}</strong>
            <FormGroup>
              <FormControlLabel
                control={<Checkbox checked={showSystemTasks} onChange={(e) => setShowSystemTasks(e.target.checked)} />}
                label="Automatic reminders"
              />
              <FormControlLabel
                control={<Checkbox checked={onlyWithHacs} onChange={(e) => setOnlyWithHacs(e.target.checked)} />}
                label="My RRC members"
              />
            </FormGroup>
          </div>

          <div>
            <strong>{t('calendar-filter-2')}</strong>
            <FormGroup>
              {categories.map((c) => (
                <FormControlLabel
                  key={String(c.id)}
                  className={"cat-" + c.id}
                  control={
                    <Checkbox
                      checked={Boolean(catChecks?.[c.id])}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setCatChecks((prev) => ({ ...(prev || {}), [c.id]: checked }));
                      }}
                    />
                  }
                  label={c.label}
                />
              ))}
            </FormGroup>
          </div>
        </div>
      </aside>

      <main>
        <header>
          <div></div>

          <div className="today-selector">
            <button className="btn text" type="button" onClick={handleTodayClick}>
              <span className="icon-calendar"></span>Today
            </button>
          </div>

          <div className="week-selector">
            <button
              type="button"
              onClick={() => setSelectedDate((prev) => prev.subtract(7, 'day'))}
              aria-label="Previous week"
            >
              <span className="icon-chevron-left"></span>
            </button>

            <span>{weekLabel}</span>

            <button type="button" onClick={() => setSelectedDate((prev) => prev.add(7, 'day'))} aria-label="Next week">
              <span className="icon-chevron-right"></span>
            </button>
          </div>
        </header>

        <div className="week" ref={weekRef} style={{ '--allday-h': `${allDayHeight}px` }}>
          <div className="hours">
            <div className="hours-header-spacer" />
            <div className="hour hour--allday" data-hour="all-day">
              All day
            </div>
            {Array.from({ length: 22 - 7 + 1 }, (_, i) => i + 7).map((h) => (
              <div className="hour" data-hour={h} key={h}>
                {String(h)}
              </div>
            ))}
          </div>

          <div className="days">
            {weekDays.map((day) => {
              const isCurrent = selectedDate && selectedDate.isSame(day, 'day');
              const dayKey = day.format('YYYY-MM-DD');
              const dayTasks = tasks[dayKey] ?? { allDay: [], hours: {} };

              return (
                <div className={`day-column ${isCurrent ? 'current' : ''}`} key={dayKey}>
                  <div className="day-header">
                    <span className="day-text">{day.format('ddd')}</span>
                    <span className="day-number">{day.format('DD')}</span>
                  </div>

                  <div className="day-grid">
                    <div className="allday-cell">
                      {dayTasks.allDay.map((taskItem) => {
                        const cat = Number(taskItem?.raw?.category_id ?? taskItem?.category ?? 0);
                        const safeCat = Number.isFinite(cat) ? cat : 0;
                        const completedClass = isCompletedRaw(taskItem?.raw) ? 'completed' : '';

                        return (
                          <div
                            key={taskItem.id}
                            className={`task ${taskItem.color || ''} cat-${safeCat} ${completedClass}`}
                            role="button"
                            tabIndex={0}
                            onClick={() => openTaskView(taskItem)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') openTaskView(taskItem);
                            }}
                          >
                            <span className="task-text">{taskItem.text}</span>
                          </div>
                        );
                      })}
                    </div>

                    {Array.from({ length: 22 - 7 + 1 }, (_, i) => i + 7).map((hourIndex) => {
                      const hourTasks = dayTasks.hours?.[String(hourIndex)] ?? [];
                      return (
                        <div key={hourIndex}>
                          {hourTasks.map((taskItem) => {
                            const cat = Number(taskItem?.raw?.category_id ?? taskItem?.category ?? 0);
                            const safeCat = Number.isFinite(cat) ? cat : 0;
                            const completedClass = isCompletedRaw(taskItem?.raw) ? 'completed' : '';

                            return (
                              <div
                                key={taskItem.id}
                                className={`task ${taskItem.color || ''} cat-${safeCat} ${completedClass}`}
                                role="button"
                                tabIndex={0}
                                onClick={() => openTaskView(taskItem)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') openTaskView(taskItem);
                                }}
                              >
                                <span className="task-time">{taskItem.timeLabel || ''}</span>
                                <span className="task-text">{taskItem.text}</span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>

      <ModalTask open={modalOpenTask} onClose={closeModalTask} task={task} onSaved={refreshWeek} />

      <ModalTaskView
        open={modalOpenTaskView}
        onClose={closeModalTaskView}
        task={taskView}
        onEdit={handleEditFromView}
        onRemove={handleRemoveFromView}
        onCompletedChanged={refreshWeek}
      />

      <ModalWindow
        open={openRemoveModal}
        handleClose={closeRemoveModal}
        entityId={removeEntityId}
        userId={decodedToken?.id}
        executeFunction={executeDeleteTask}
        postExecuteFunction={postDeleteTask}
        title="Remove Task"
        text="Are you sure you want to remove this task?"
        textButtonCancel="cancel"
        textoButtonConfirm="remove"
        feedback="removed"
        extraInfo={null}
      />
    </div>
  );
}

export default Calendar;
